import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { formatLeagueDateTime } from "utils/league-datetime";
import { useIntl } from "react-intl";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { leagueCustomerDashboardRoute } from "network/api-routes";
import { ROUTES } from "constants/routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionAccess } from "hooks/useGestionAccess";
import { useUserContext } from "components/hoc/withUserContext";
import GestionTable from "components/pages/Gestion/GestionTable";
import GestionPageHeader from "components/pages/Gestion/GestionPageHeader";
import CreateTournamentButton from "components/pages/Gestion/CreateTournamentButton";
import FinanceOnly from "components/pages/Gestion/FinanceOnly";
import { formatFinancialNumber } from "components/pages/Gestion/financial/financial-helpers";

const MatchTable = ({ matches, emptyMessage, emptyAction, planilleroLink, intl }) => {
  if (!matches?.length) {
    return (
      <Stack spacing={1} sx={{ py: 2 }}>
        <RevaText variant="body2" color="text.secondary">
          {emptyMessage}
        </RevaText>
        {emptyAction}
      </Stack>
    );
  }

  return (
    <GestionTable
      headers={[
        { key: "datetime", title: intl.formatMessage({ id: "gestion.dashboard.col.datetime" }) },
        { key: "matchday", title: intl.formatMessage({ id: "gestion.dashboard.col.matchday" }) },
        { key: "match", title: intl.formatMessage({ id: "gestion.dashboard.col.match" }) },
        { key: "tournament", title: intl.formatMessage({ id: "gestion.dashboard.col.tournament" }) },
        {
          key: "actions",
          title: intl.formatMessage({ id: "gestion.actions" }),
          align: "right",
          renderCell: (_value, match) => {
            const url = planilleroLink(match);
            if (!url) return null;
            return (
              <Tooltip title={intl.formatMessage({ id: "league.planillero.open" })}>
                <IconButton size="small" component={RouterLink} to={url}>
                  <AssignmentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          },
        },
      ]}
      rows={matches.map((match) => ({
        ...match,
        datetime: match.scheduled_time ? formatLeagueDateTime(match.scheduled_time, match.tournament_timezone) : "—",
        matchday: match.matchday_name || "—",
        match: `${match.local_team_name || "—"} vs ${match.visiting_team_name || "—"}`,
        tournament: match.tournament_name || "—",
      }))}
    />
  );
};

const GestionDashboard = () => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useUserContext();
  const { can } = useGestionAccess();
  const canTournaments = can("tournaments");
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dashboard, setDashboard] = useState(null);

  const loadDashboard = useCallback(() => {
    if (!customerId || !user) return;
    setLoading(true);
    setError(false);

    performRequest("GET", leagueCustomerDashboardRoute(customerId), {}, true, user)
      .then((response) => {
        setDashboard(response.data);
      })
      .catch((err) => {
        setDashboard(null);
        setError(true);
        enqueueSnackbar(err?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [customerId, enqueueSnackbar, intl, user]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const planilleroLink = useCallback(
    (match) => {
      // The planillero needs the Torneos area; finance-only administrators get no link.
      if (!canTournaments) return null;
      const tournamentKey = match.tournament_uuid;
      if (!tournamentKey) return null;
      return {
        pathname: `${ROUTES.PUBLIC.TOURNAMENT}/${tournamentKey}/planillero/${match.id}`,
        state: {
          gestionCustomerId: customerId,
          gestionReturnPath: GESTION_ROUTES.dashboard(customerId),
        },
      };
    },
    [canTournaments, customerId],
  );

  if (!customerId) return null;

  const displayName = dashboard?.customer?.name || customerName || "";
  const upcoming = dashboard?.upcoming_matches || [];
  const pending = dashboard?.pending_planillas || [];
  const financial = dashboard?.financial;
  const inventory = dashboard?.inventory;

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <GestionPageHeader
          title={intl.formatMessage({ id: "gestion.dashboard.title" }, { name: displayName })}
          subtitle={intl.formatMessage({ id: "gestion.dashboard.subtitle" })}
          actions={
            <>
              <CreateTournamentButton customerId={customerId} variant="contained" color="secondary" size="small">
                {intl.formatMessage({ id: "gestion.dashboard.action.createCompetition" })}
              </CreateTournamentButton>
              {canTournaments ? (
                <>
                  <Button
                    component={RouterLink}
                    to={GESTION_ROUTES.matches(customerId)}
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    {intl.formatMessage({ id: "gestion.dashboard.action.matches" })}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={GESTION_ROUTES.teams(customerId)}
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    {intl.formatMessage({ id: "gestion.dashboard.action.addTeam" })}
                  </Button>
                  <Button
                    component={RouterLink}
                    to={GESTION_ROUTES.players(customerId)}
                    variant="outlined"
                    color="primary"
                    size="small"
                  >
                    {intl.formatMessage({ id: "gestion.dashboard.action.addPlayer" })}
                  </Button>
                </>
              ) : null}
            </>
          }
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadDashboard}>
                {intl.formatMessage({ id: "gestion.dashboard.retry" })}
              </Button>
            }
          >
            {intl.formatMessage({ id: "gestion.dashboard.loadError" })}
          </Alert>
        ) : (
          <>
            <Box>
              <RevaText variant="h6" sx={{ mb: 1 }}>
                {intl.formatMessage({ id: "gestion.dashboard.upcoming.title" })}
              </RevaText>
              <MatchTable
                matches={upcoming}
                emptyMessage={intl.formatMessage({ id: "gestion.dashboard.upcoming.empty" })}
                emptyAction={
                  canTournaments ? (
                    <MuiLink component={RouterLink} to={GESTION_ROUTES.matches(customerId)} underline="hover">
                      {intl.formatMessage({ id: "gestion.dashboard.upcoming.viewAll" })}
                    </MuiLink>
                  ) : null
                }
                planilleroLink={planilleroLink}
                intl={intl}
              />
            </Box>

            <Box>
              <RevaText variant="h6" sx={{ mb: 1 }}>
                {intl.formatMessage({ id: "gestion.dashboard.pending.title" })}
              </RevaText>
              <MatchTable
                matches={pending}
                emptyMessage={intl.formatMessage({ id: "gestion.dashboard.pending.empty" })}
                planilleroLink={planilleroLink}
                intl={intl}
              />
            </Box>

            {financial?.enabled ? (
              <FinanceOnly>
                <Box>
                  <RevaText variant="h6" sx={{ mb: 1 }}>
                    {intl.formatMessage({ id: "gestion.dashboard.debts.title" })}
                  </RevaText>
                  <RevaText variant="body1" sx={{ mb: 1 }}>
                    {intl.formatMessage(
                      { id: "gestion.dashboard.debts.total" },
                      {
                        amount: formatFinancialNumber(financial.total_debt, financial.currency),
                        currency: financial.currency || "",
                      },
                    )}
                  </RevaText>
                  {Number(financial.scheduled_debt) > 0 ? (
                    <RevaText variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {intl.formatMessage(
                        { id: "gestion.dashboard.debts.scheduled" },
                        {
                          amount: formatFinancialNumber(financial.scheduled_debt, financial.currency),
                          currency: financial.currency || "",
                        },
                      )}
                    </RevaText>
                  ) : null}
                  {financial.top_debtors?.length ? (
                    <GestionTable
                      headers={[
                        { key: "entity", title: intl.formatMessage({ id: "gestion.dashboard.debts.entity" }) },
                        {
                          key: "amount",
                          title: intl.formatMessage({ id: "gestion.dashboard.debts.amount" }),
                          align: "right",
                        },
                        {
                          key: "invoices",
                          title: intl.formatMessage({ id: "gestion.dashboard.debts.invoices" }),
                          align: "right",
                        },
                      ]}
                      rows={financial.top_debtors.map((debtor) => ({
                        id: debtor.entity_id,
                        entity: debtor.entity_name,
                        amount: formatFinancialNumber(debtor.total_debt, financial.currency),
                        invoices: debtor.invoice_count,
                      }))}
                    />
                  ) : (
                    <RevaText variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: "gestion.dashboard.debts.empty" })}
                    </RevaText>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <MuiLink component={RouterLink} to={GESTION_ROUTES.financials(customerId)} underline="hover">
                      {intl.formatMessage({ id: "gestion.dashboard.debts.viewFinancials" })}
                    </MuiLink>
                  </Box>
                </Box>
              </FinanceOnly>
            ) : null}

            {inventory ? (
              <Box>
                <RevaText variant="h6" sx={{ mb: 1 }}>
                  {intl.formatMessage({ id: "gestion.dashboard.inventory.title" })}
                </RevaText>
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  {[
                    ["teams", "gestion.dashboard.inventory.teams"],
                    ["players", "gestion.dashboard.inventory.players"],
                    ["tournaments", "gestion.dashboard.inventory.tournaments"],
                  ].map(([key, id]) => (
                    <Box key={key}>
                      <RevaText variant="h4">{inventory[key] ?? 0}</RevaText>
                      <RevaText variant="body2" color="text.secondary">
                        {intl.formatMessage({ id })}
                      </RevaText>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default GestionDashboard;
